"use client";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import Keys from "@bookingcom/bui-core/constants/keys";
import { normalizeKey } from "@bookingcom/bui-core/utilities/helpers";
import Popover from "../Popover/index.js";
import Stack from "../Stack/index.js";
import ListItem from "../ListItem/index.js";
var AutocompleteContext = React.createContext({});
var useAutocomplete = function () { return React.useContext(AutocompleteContext); };
var AutocompleteTrigger = function (props) {
    var children = props.children;
    var _a = useAutocomplete(), triggerRef = _a.triggerRef, positionRef = _a.positionRef, open = _a.open, active = _a.active, onChange = _a.onChange;
    var handleChange = function (e) {
        onChange === null || onChange === void 0 ? void 0 : onChange({ value: e.target.value });
        open();
    };
    React.useEffect(function () {
        var triggerEl = triggerRef.current;
        if (!triggerEl)
            return;
        var handleKeydown = function (e) {
            var key = normalizeKey(e.key);
            if (key !== Keys.DOWN)
                return;
            if (active)
                return;
            open();
        };
        triggerEl.addEventListener("keydown", handleKeydown);
        return function () { return triggerEl.removeEventListener("keydown", handleKeydown); };
    }, [triggerRef, open, active]);
    return children({
        ref: triggerRef,
        onChange: handleChange,
        autoComplete: "off",
        role: "combobox",
    }, positionRef);
};
var AutocompletePopover = function (props) {
    var children = props.children;
    var _a = useAutocomplete(), triggerRef = _a.triggerRef, positionRef = _a.positionRef, active = _a.active, open = _a.open, close = _a.close;
    var hasChildren = !!React.Children.toArray(children).filter(Boolean).length;
    return (React.createElement(Popover, { position: "bottom-stretch", triggerRef: triggerRef, positionRef: positionRef, triggerType: "focus", trapFocusMode: "soft", navigationMode: "arrows", hideClose: true, hideArrow: true, fill: true, 
        /**
         * Disabling the animation here to simplify the hiding of the dropdown when all items get filtered out
         * We don't expect product teams to handle this behaviour on the product each time
         */
        disableAnimation: "hide", active: hasChildren ? active : false, onOpen: open, onClose: close },
        React.createElement(Popover.Content, null,
            React.createElement(Stack, { gap: 0.5, mixin: { padding: 2 } }, children))));
};
var AutocompleteItem = function (props) {
    var value = props.value, listItemProps = __rest(props, ["value"]);
    var onItemClick = useAutocomplete().onItemClick;
    var handleClick = function () {
        onItemClick({ value: value });
    };
    return (React.createElement(ListItem, __assign({}, listItemProps, { onClick: handleClick, attributes: { role: "option" }, roundedCorners: true, spacing: "small" })));
};
var Autocomplete = function (props) {
    var children = props.children, onChange = props.onChange;
    var _a = React.useState(false), active = _a[0], setActive = _a[1];
    var triggerRef = React.useRef(null);
    var positionRef = React.useRef(null);
    var open = React.useCallback(function () {
        setActive(true);
    }, []);
    var close = React.useCallback(function () {
        setActive(false);
    }, []);
    var handleItemClick = function (args) {
        onChange === null || onChange === void 0 ? void 0 : onChange(args);
        close();
    };
    return (React.createElement(AutocompleteContext.Provider, { value: {
            active: active,
            open: open,
            close: close,
            triggerRef: triggerRef,
            positionRef: positionRef,
            onChange: onChange,
            onItemClick: handleItemClick,
        } }, children));
};
Autocomplete.Trigger = AutocompleteTrigger;
Autocomplete.Popover = AutocompletePopover;
Autocomplete.Item = AutocompleteItem;
export default Autocomplete;
